import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEmployeeInformation,
  updateDeclarationField,
  setDeclarationImage,
  removeDeclarationImage,
} from "../../../Redux/employeeInformationSlice.js";
import styles from "./DeclarationForm.module.scss";

const familyOptions = [
  "FATHER",
  "MOTHER",
  "SPOUSE",
  "SON",
  "DAUGHTER",
  "GUARDIAN",
];

const DeclarationForm = forwardRef(function DeclarationForm(_, ref) {
  const dispatch = useDispatch();

  const {
    declarationDetails: form,
    declarationImages,
    basicInformation,
    bankDetails,
  } = useSelector(selectEmployeeInformation);

  const [errorFields, setErrorFields] = useState(new Set());

  const clearError = (name) => {
    setErrorFields((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const updateField = (name, value) => {
    dispatch(updateDeclarationField({ name, value }));
    clearError(name);
  };

  // Auto-filled values from previous sections
  const employeeName = basicInformation.first_name || "";
  const mobileNo = basicInformation.phoneNum || "";
  const email = basicInformation.email_id || form.email || "";
  const dateOfBirth = basicInformation.DateofBirth || "";
  const gender = basicInformation.gender || form.gender || "";
  const aadhaarNumber = basicInformation.aadhar_number || "";

  const bankAccount = bankDetails.account_number || "";
  const ifscCode = bankDetails.ifsc_code || "";

  const handleFamilySelect = (value) => {
    updateField("relation", value);
    updateField("relation_name", "");
  };

  const clearPerson = () => {
    updateField("relation", "");
    updateField("relation_name", "");
  };

  const handleImage = (file) => {
    if (!file || file.size > 10 * 1024 * 1024) return;

    if (declarationImages.passport?.url) {
      URL.revokeObjectURL(declarationImages.passport.url);
    }

    dispatch(
      setDeclarationImage({
        name: "passport",
        image: {
          name: file.name,
          file,
          url: URL.createObjectURL(file),
        },
      }),
    );

    clearError("passport");
  };

  const removeImage = () => {
    if (declarationImages.passport?.url) {
      URL.revokeObjectURL(declarationImages.passport.url);
    }

    dispatch(removeDeclarationImage("passport"));
  };

  const getMissingFields = useCallback(() => {
    const missing = [];
    const missingNames = new Set();

    if (!String(form.relation || "").trim()) {
      missing.push("Family Member");
      missingNames.add("relation");
    }

    if (!String(form.relation_name || "").trim()) {
      missing.push("Person Name");
      missingNames.add("relation_name");
    }

    if (form.is_international) {
      if (!String(form.origin_country || "").trim()) {
        missing.push("Country of Origin");
        missingNames.add("origin_country");
      }

      if (!String(form.passport_number || "").trim()) {
        missing.push("Passport Number");
        missingNames.add("passport_number");
      }

      if (!form.passport_validity_from) {
        missing.push("Passport Validity From");
        missingNames.add("passport_validity_from");
      }

      if (!form.passport_validity_to) {
        missing.push("Passport Validity To");
        missingNames.add("passport_validity_to");
      }
    }

    setErrorFields(missingNames);
    return missing;
  }, [form]);

  useImperativeHandle(ref, () => ({
    getMissingFields,
    isComplete: () => getMissingFields().length === 0,
  }));

  return (
    <div className={styles.declaration}>
      <header className={styles.sectionHeader}>Declaration Form</header>

      <div className={styles.content}>
        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>Employee Name</span>
            <input value={employeeName} disabled />
          </label>

          <label className={styles.field}>
            <span>
              Select Family Member<b>*</b>
            </span>
            <select
              value={form.relation || ""}
              className={errorFields.has("relation") ? styles.inputError : ""}
              onChange={(e) => handleFamilySelect(e.target.value)}
            >
              <option value="">Select</option>
              {familyOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errorFields.has("relation") && (
              <p className={styles.fieldError}>Please select a family member</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              Person Name<b>*</b>
            </span>
            <div className={styles.inlineField}>
              <input
                value={form.relation_name || ""}
                disabled={!form.relation}
                placeholder="Selected family member name"
                className={
                  errorFields.has("relation_name") ? styles.inputError : ""
                }
                onChange={(e) =>
                  updateField("relation_name", e.target.value.toUpperCase())
                }
              />
              {form.relation_name && (
                <button
                  type="button"
                  className={styles.closeBtn}
                  onClick={clearPerson}
                >
                  ×
                </button>
              )}
            </div>
            {errorFields.has("relation_name") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          <label className={styles.field}>
            <span>Date of Birth</span>
            <input value={dateOfBirth} disabled />
          </label>

          <label className={styles.field}>
            <span>Gender</span>
            <input value={gender} disabled />
          </label>

          <label className={styles.field}>
            <span>Marital Status</span>
            <select
              value={form.marital_status || "unmarried"}
              onChange={(e) => updateField("marital_status", e.target.value)}
            >
              <option value="unmarried">UNMARRIED</option>
              <option value="married">MARRIED</option>
              <option value="others">OTHERS</option>
            </select>
          </label>

          <label className={styles.field}>
            <span>Email</span>
            <input
              value={form.email || email}
              onChange={(e) => updateField("email", e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>Mobile No</span>
            <input value={mobileNo} disabled />
          </label>

          <label className={styles.field}>
            <span>Universal Account Number</span>
            <input
              value={form.universal_acc_number || ""}
              onChange={(e) =>
                updateField("universal_acc_number", e.target.value)
              }
            />
          </label>

          <label className={styles.field}>
            <span>Previous PF Account Number</span>
            <input
              value={form.pf_acc_number || ""}
              onChange={(e) => updateField("pf_acc_number", e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>Date of Exit From Previous Employment</span>
            <input
              type="date"
              value={form.previous_end_date || ""}
              onChange={(e) => updateField("previous_end_date", e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>Scheme Certificate Number</span>
            <input
              value={form.scheme_number || ""}
              onChange={(e) => updateField("scheme_number", e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>Pension Payment Order</span>
            <input
              value={form.ppo_number || ""}
              onChange={(e) => updateField("ppo_number", e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>Earlier PF Member</span>
            <div className={styles.inlineOptions}>
              {[true, false].map((value) => (
                <label key={String(value)}>
                  <input
                    type="radio"
                    checked={form.provident_fund_schema === value}
                    onChange={() => updateField("provident_fund_schema", value)}
                  />
                  {value ? "Yes" : "No"}
                </label>
              ))}
            </div>
          </label>

          <label className={styles.field}>
            <span>Earlier Pension Scheme 1995</span>
            <div className={styles.inlineOptions}>
              {[true, false].map((value) => (
                <label key={String(value)}>
                  <input
                    type="radio"
                    checked={form.pension_schema === value}
                    onChange={() => updateField("pension_schema", value)}
                  />
                  {value ? "Yes" : "No"}
                </label>
              ))}
            </div>
          </label>

          <label className={styles.field}>
            <span>International Workers</span>
            <div className={styles.inlineOptions}>
              {[true, false].map((value) => (
                <label key={String(value)}>
                  <input
                    type="radio"
                    checked={form.is_international === value}
                    onChange={() => {
                      updateField("is_international", value);

                      if (!value) {
                        updateField("origin_country", "");
                        updateField("passport_number", "");
                        updateField("passport_validity_from", null);
                        updateField("passport_validity_to", null);
                      }
                    }}
                  />
                  {value ? "Yes" : "No"}
                </label>
              ))}
            </div>
          </label>

          {form.is_international && (
            <>
              <label className={styles.field}>
                <span>
                  Country of Origin<b>*</b>
                </span>
                <input
                  value={form.origin_country || ""}
                  className={
                    errorFields.has("origin_country") ? styles.inputError : ""
                  }
                  onChange={(e) =>
                    updateField("origin_country", e.target.value.toUpperCase())
                  }
                />
              </label>

              <label className={styles.field}>
                <span>
                  Passport Number<b>*</b>
                </span>
                <input
                  value={form.passport_number || ""}
                  className={
                    errorFields.has("passport_number") ? styles.inputError : ""
                  }
                  onChange={(e) =>
                    updateField("passport_number", e.target.value.toUpperCase())
                  }
                />
              </label>

              <label className={styles.field}>
                <span>
                  Validity of Passport (From)<b>*</b>
                </span>
                <input
                  type="date"
                  value={form.passport_validity_from || ""}
                  className={
                    errorFields.has("passport_validity_from")
                      ? styles.inputError
                      : ""
                  }
                  onChange={(e) =>
                    updateField("passport_validity_from", e.target.value)
                  }
                />
              </label>

              <label className={styles.field}>
                <span>
                  Validity of Passport (To)<b>*</b>
                </span>
                <input
                  type="date"
                  value={form.passport_validity_to || ""}
                  className={
                    errorFields.has("passport_validity_to")
                      ? styles.inputError
                      : ""
                  }
                  onChange={(e) =>
                    updateField("passport_validity_to", e.target.value)
                  }
                />
              </label>
            </>
          )}

          <label className={styles.field}>
            <span>Bank Account No</span>
            <input value={bankAccount} disabled />
          </label>

          <label className={styles.field}>
            <span>IFSC Code</span>
            <input value={ifscCode} disabled />
          </label>

          <label className={styles.field}>
            <span>Aadhaar Number</span>
            <input value={aadhaarNumber} disabled />
          </label>

          <label className={styles.field}>
            <span>PAN Number</span>
            <input
              value={form.pan_number || ""}
              onChange={(e) =>
                updateField("pan_number", e.target.value.toUpperCase())
              }
            />
          </label>
        </div>

        <div className={styles.uploadBlock}>
          <span>Passport Image</span>

          <p className={styles.note}>
            Note: Upload the passport image in JPG, JPEG, or PNG format. Maximum
            size: 10 MB.
          </p>

          {declarationImages.passport ? (
            <div className={styles.previewRow}>
              <img
                src={declarationImages.passport.url}
                alt="Passport preview"
                className={styles.imagePreview}
              />
              <button
                type="button"
                className={styles.removeButton}
                onClick={removeImage}
              >
                Remove Image
              </button>
            </div>
          ) : form.passport ? (
            <div className={styles.previewRow}>
              <img
                src={form.passport}
                alt="Passport preview"
                className={styles.imagePreview}
              />
            </div>
          ) : (
            <label className={styles.uploadButton}>
              Upload Image
              <input
                type="file"
                className={styles.hiddenInput}
                accept="image/png,image/jpeg,image/jpg"
                onChange={(e) => handleImage(e.target.files?.[0])}
              />
            </label>
          )}
        </div>
      </div>
    </div>
  );
});

export default DeclarationForm;
